import { useContext, useEffect, useRef, useState } from 'react';
import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import { DataPoint } from '../../types';
import { DasSenseH } from '../../types/Device';
import {
  WebSocketCoordinates,
  WebSocketDasSenseHDataPoint,
} from '../../types/Websocket';

import { getProjectDasSensesH } from '../../apis/DasSenseHApi';

import { PubSubContext } from '../../utils/PubSub';

type UseProjectDasloopMapParams = {
  queryKey: QueryKey;
  projectId: string | null | undefined;
  indexBy?: 'id' | 'dasId';
  orderBy?: 'das_id' | 'last_data_point_received_at';
  queryOptions?: {
    enabled?: boolean;
  };
  onProgress?: (progress) => void;
};

export type DasSenseHMap = { [id: string]: DasSenseH | undefined };

/* Important:
 * Plese use this hook with follow to get all devices
 *  useEffect(() => {
 *   let timer: NodeJS.Timeout;
 *  if (isFetchingDasloopMap === false && hasNextPage) {
 *    timer = setTimeout(() => {
 *      fetchNextPage();
 *    }, 200);
 *
 *    return () => {
 *      clearTimeout(timer);
 *    };
 *  }
 * }, [hasNextPage, isFetchingDasloopMap]);
 * Otherwise it will let queue size bigger and bigger and do not update the deveices
 */

export const useProjectDasSenseHMapWithPages = ({
  projectId,
  indexBy = 'id',
  orderBy,
  queryKey,
  queryOptions,
  onProgress,
}: UseProjectDasloopMapParams) => {
  const dataPointQueueRef = useRef<WebSocketDasSenseHDataPoint[]>([]);
  const coordinatesQueueRef = useRef<WebSocketCoordinates[]>([]);

  const pubSub = useContext(PubSubContext);
  const [dasSenseHMap, setDasSenseHMap] = useState<DasSenseHMap>({});
  const [total, setTotal] = useState(0);

  const { data, ...others } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const res = await getProjectDasSensesH({
        projectId: projectId as string,
        params: {
          orderBy,
          nextCursor: pageParam,
        },
      });
      setTotal((origin) => {
        if (origin !== res.data.metadata.total) {
          return res.data.metadata.total;
        }

        return origin;
      });
      return res.data;
    },
    getNextPageParam: (res) => res.paging.nextCursor || undefined,
    enabled: !!projectId && (queryOptions?.enabled ?? true),
    refetchOnWindowFocus: false,
    initialPageParam: undefined,
  });

  useEffect(() => {
    if (data) {
      const newDasSenseHMap: DasSenseHMap = {};
      let count = 0;
      data.pages
        .map((page) => page.data)
        .flat()
        .forEach((dasSenseH) => {
          count++;
          if (indexBy === 'dasId') {
            newDasSenseHMap[dasSenseH.dasId] = dasSenseH;
          } else if (indexBy === 'id') {
            newDasSenseHMap[dasSenseH.id] = dasSenseH;
          }
        });
      setDasSenseHMap((origin) => ({ ...newDasSenseHMap, ...origin }));
      onProgress?.((count / (total == 0 ? 1 : total)) * 100);
    }
  }, [data, indexBy, total]);

  useEffect(() => {
    const dataPointHandler = (datapointData: WebSocketDasSenseHDataPoint) => {
      if (projectId === datapointData.endpoint.metadata['dsm/projectId']) {
        dataPointQueueRef.current.push(datapointData);
      }
    };

    const coordinatesHandler = (coordinatesData: WebSocketCoordinates) => {
      if (projectId === coordinatesData.endpoint.metadata['dsm/projectId']) {
        coordinatesQueueRef.current.push(coordinatesData);
      }
    };

    pubSub?.subscribe('dassenses:data-point-received', dataPointHandler);
    pubSub?.subscribe('coordinates-updated', coordinatesHandler);

    return () => {
      pubSub?.unsubscribe('dassenses:data-point-received', dataPointHandler);
      pubSub?.unsubscribe('coordinates-updated', coordinatesHandler);
    };
  }, [pubSub, projectId, indexBy]);

  useEffect(() => {
    const dataTotal = data?.pages.map((page) => page.data).flat().length ?? 0;

    if (dataTotal === total && total !== 0) {
      const dataPointQueueTimer = setInterval(() => {
        const dataPoints: WebSocketDasSenseHDataPoint[] = [];

        while (dataPointQueueRef.current.length > 0) {
          const newDataPoint =
            dataPointQueueRef.current.shift() as WebSocketDasSenseHDataPoint;
          dataPoints.push(newDataPoint);
        }

        if (dataPoints.length > 0) {
          setDasSenseHMap((origin) => {
            const newDasSenseHMap = { ...origin };
            dataPoints
              .filter((datapointData) => {
                return !!origin[datapointData.endpoint[indexBy]];
              })
              .map((datapointData) => {
                const newDasSenseH = origin[datapointData.endpoint[indexBy]];

                if (newDasSenseH) {
                  const updateDataPoint: { [key: string]: DataPoint<any> } =
                    Object.entries(datapointData.dataPoint).reduce(
                      (prev, [key, value]) => {
                        if (value !== undefined && value !== null) {
                          prev[key] = {
                            timestamp: datapointData.timestamp,
                            value,
                            stale: false,
                          };
                        }
                        return prev;
                      },
                      {},
                    );

                  newDasSenseH.shadow.dataPoint = {
                    ...newDasSenseH.shadow.dataPoint,
                    ...updateDataPoint,
                  };

                  // Newer firmware no longer reports shutdown; a stale value
                  // from an old data point must not permanently lock the
                  // device into 'stop' status once it stops being reported.
                  if (!('shutdown' in datapointData.dataPoint)) {
                    delete newDasSenseH.shadow.dataPoint.shutdown;
                  }

                  newDasSenseHMap[newDasSenseH[indexBy]] = newDasSenseH;
                }
              });

            return newDasSenseHMap;
          });
        }
      }, 1000);

      const coordinatesQueueTimer = setInterval(() => {
        const coords: WebSocketCoordinates[] = [];

        while (coordinatesQueueRef.current.length > 0) {
          const webSocketCoordinates =
            coordinatesQueueRef.current.shift() as WebSocketCoordinates;
          coords.push(webSocketCoordinates);
        }

        if (coords.length > 0) {
          setDasSenseHMap((origin) => {
            const newDasSenseHMap = { ...origin };
            coords
              .filter((coord) => {
                return !!origin[coord.endpoint[indexBy]];
              })
              .map((coord) => {
                const newDasSenseH = origin[coord.endpoint[indexBy]];

                if (newDasSenseH) {
                  // If device has offWorkInfo and receives new coordinates, clear the offWorkInfo

                  newDasSenseH.coordinates = coord.coordinates;
                  newDasSenseH.coordinatesSource = coord.source;
                  newDasSenseH.lastCoordinatesUpdatedAt = coord.timestamp;
                  newDasSenseHMap[newDasSenseH[indexBy]] = newDasSenseH;
                }
              });

            return newDasSenseHMap;
          });
        }
      }, 1000);

      return () => {
        clearInterval(dataPointQueueTimer);
        clearInterval(coordinatesQueueTimer);
      };
    }
  }, [data, total, indexBy]);

  return {
    data: dasSenseHMap,
    ...others,
  };
};
